import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import moment from "moment";

import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import {
  trackMetricOptions,
  type TTrackMetric,
} from "@screens/FieldsScreen/fieldTracksLayer";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import Dropdown from "@components/MapDropdown";
import { useLang } from "@src/hooks/useLang";
import { useStores } from "@stores";
import FieldTracksFilters from "@screens/FieldsScreen/FieldTracksFilters";
import FieldTracksExport from "@screens/FieldsScreen/FieldTracksExport";
import FieldTrackTimeline from "@screens/FieldsScreen/FieldTrackTimeline";
import {
  HideLegendButton,
  ShowLegendButton,
} from "@screens/FieldsScreen/MapYieldLegend";
import useIsMobile from "@src/hooks/useIsMobile";
import { ReactComponent as ExpandMoreIcon } from "@assets/icons/expand_more.svg";

interface IProps {}

const Root = styled.div`
  position: absolute;
  inset: 18px max(52px, env(safe-area-inset-right, 52px)) auto auto;
  background: #fff;
  padding: 10px 12px;
  width: 232px;
  max-height: calc(100% - 120px);
  overflow-y: auto;
  border-radius: 6px;
  z-index: 2;
  display: flex;
  flex-direction: column;
`;

const pulse = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.45; }
  100% { opacity: 1; }
`;

/** A worked day: a swatch (its colour in Date mode), the date, and a pulse
 *  while its batch is still in flight. Every day is drawn until one is
 *  clicked; that day is then read alone, by time of pass. */
const DayRow = styled.button<{ pending: boolean; focusedDay: boolean; dimmed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 3px 4px;
  border: none;
  border-radius: 4px;
  background: ${({ focusedDay }) => (focusedDay ? "#f0f2f3" : "none")};
  cursor: pointer;
  text-align: left;
  opacity: ${({ dimmed }) => (dimmed ? 0.4 : 1)};
  &:hover {
    background: #f0f2f3;
  }
  ${({ pending }) =>
    pending ? `animation: ${pulse} 1.1s ease-in-out infinite;` : ""}
`;

const TimeBlock = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eef1f0;
`;

const Bar = styled.div`
  height: 4px;
  border-radius: 2px;
  background: #eef1f0;
  overflow: hidden;
  margin: 8px 0 4px 0;
`;

const BarFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${({ pct }) => pct}%;
  background: #4caf50;
  transition: width 0.25s ease;
`;

const DeviceRow = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 2px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  opacity: ${({ active }) => (active ? 1 : 0.4)};
`;

const Swatch = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
  background: ${({ color }) => color};
`;

const Toggle = styled.div`
  display: flex;
  border: 1px solid #e0e2e7;
  border-radius: 4px;
  overflow: hidden;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  flex: 1;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  padding: 4px 6px;
  border: none;
  cursor: pointer;
  background: ${({ active }) => (active ? "#f0f2f3" : "#fff")};
  color: ${({ active }) => (active ? "#494C5B" : "#8c8f9b")};
  font-weight: ${({ active }) => (active ? 600 : 400)};
`;

/** Same look as the export trigger it sits under. */
const ReloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  padding: 5px 6px;
  border: 1px solid #e0e2e7;
  border-radius: 4px;
  background: #fff;
  color: #494c5b;
  cursor: pointer;
  &:disabled {
    cursor: default;
    color: #b4b7c0;
  }
  svg {
    width: 12px;
    height: 12px;
  }
`;

const Pulsing = styled.div`
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text size={12} color="#8c8f9b">
    {children}
  </Text>
);

/**
 * Controls for the field-tracks layer: which days, which machines, which
 * metric, and how the swaths are colored.
 *
 * Deliberately mirrors the Tracks screen's controls so the two feel like one
 * feature — the metric list, the device swatches and the value/device toggle
 * are the same choices in the same order.
 */
const FieldTracksPanel: React.FC<IProps> = () => {
  const lang = useLang();
  const vm = useFieldsScreenVM();
  const { accountStore } = useStores();
  const isMobile = useIsMobile();
  // Same as the yield legend: in the split view the panel covers half of a
  // narrow map, so desktop can tuck it away.
  const [hidden, setHidden] = useState(false);

  if (vm.selectedFieldView !== "tracks" || vm.selectedFieldId == null) return null;

  const devices = vm.trackDeviceList;
  const focusDay = vm.trackTime.day;

  const metricOptions = trackMetricOptions(
    vm.trackDevices,
    accountStore.cameraInstalled,
    lang
  );

  // The first scan of a season runs server-side and can take minutes. Saying
  // "no tracks" while it is still working would be plainly wrong.
  if (vm.tracksRefreshing && vm.trackDayList.length === 0) {
    return (
      <Root>
        <Text size={14} weight={500}>{lang.fields.mapSelecterTracks ?? "Tracks"}</Text>
        <SizedBox height={6} />
        <Muted>
          {lang.fields.tracksComputing ??
            "Finding this season's passes for the first time. This can take a few minutes; later visits are instant."}
        </Muted>
      </Root>
    );
  }

  if (!vm.tracksLoading && vm.trackDayList.length === 0) {
    return (
      <Root>
        <Text size={14} weight={500}>{lang.fields.mapSelecterTracks ?? "Tracks"}</Text>
        <SizedBox height={6} />
        <Muted>
          {lang.fields.tracksEmpty ??
            "No machine passes recorded on this field this season."}
        </Muted>
      </Root>
    );
  }

  if (!isMobile && hidden) {
    return (
      <ShowLegendButton onClick={() => setHidden(false)}>
        <ExpandMoreIcon />
        {lang.fields.legendShowLabel ?? "Show legend"}
      </ShowLegendButton>
    );
  }

  return (
    <Root>
      <Dropdown
        inline
        title={lang.fields.tracksMetricTitle ?? "Value"}
        options={metricOptions}
        selected={vm.trackMetric}
        onSelect={(v: string) => vm.setTrackMetric(v as TTrackMetric)}
      />

      <SizedBox height={10} />
      <Text size={12} weight={500}>
        {lang.fields.tracksColorTitle ?? "Color by"}
      </Text>
      <SizedBox height={4} />
      <Toggle>
        <ToggleButton
          active={vm.trackColorMode === "value"}
          onClick={() => vm.setTrackColorMode("value")}
        >
          {lang.fields.tracksColorValue ?? "Value"}
        </ToggleButton>
        <ToggleButton
          active={vm.trackColorMode === "device"}
          onClick={() => vm.setTrackColorMode("device")}
        >
          {lang.fields.tracksColorDevice ?? "Machine"}
        </ToggleButton>
        <ToggleButton
          active={vm.trackColorMode === "date"}
          onClick={() => vm.setTrackColorMode("date")}
        >
          {/* One day has no date sequence; the same button colours by time. */}
          {focusDay ? lang.tracksTime.color : lang.fields.tracksColorDate ?? "Date"}
        </ToggleButton>
      </Toggle>

      {focusDay && (
        <TimeBlock>
          <FieldTrackTimeline
            day={focusDay}
            timeline={vm.trackTime.timeline}
            from={vm.trackTime.from}
            to={vm.trackTime.to}
            onWindow={vm.setTrackTimeWindow}
            onClose={() => vm.focusTrackDay(focusDay)}
          />
        </TimeBlock>
      )}

      <SizedBox height={10} />
      <FieldTracksFilters />

      {devices.length > 0 && (
        <>
          <SizedBox height={10} />
          <Text size={12} weight={500}>
            {lang.fields.tracksDevicesTitle ?? "Machines"}
          </Text>
          <SizedBox height={4} />
          {devices.map((d) => (
            // Color comes from the device list itself, not from the drawn
            // legend — the legend only exists once points have loaded, which
            // is why every swatch used to render grey.
            <DeviceRow
              key={d.deviceId}
              active={vm.selectedTrackDeviceIds.includes(d.deviceId)}
              onClick={() => vm.toggleTrackDevice(d.deviceId)}
            >
              <Swatch color={d.color} />
              <Text size={12}>{d.name}</Text>
            </DeviceRow>
          ))}
        </>
      )}

      {accountStore.canExport && (
        <>
          <SizedBox height={10} />
          <FieldTracksExport />
        </>
      )}

      {/* Refetches the points, and with them each machine's current delay and
          width, so a device-settings change shows without reloading the page. */}
      <SizedBox height={6} />
      <ReloadButton
        type="button"
        disabled={vm.tracksLoading}
        onClick={() => void vm.reloadFieldTracks()}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="#68ad18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 4v6h6" /><path d="M23 20v-6h-6" />
          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
        </svg>
        {lang.tracks?.reload ?? "Reload"}
      </ReloadButton>

      <SizedBox height={10} />
      <Text size={12} weight={500}>
        {lang.fields.tracksDaysTitle ?? "Days worked"}
      </Text>
      {!focusDay && <Muted>{lang.tracksTime.hint}</Muted>}
      <SizedBox height={4} />
      {vm.trackDayLegend.map(({ day, color }) => (
        <DayRow
          key={day}
          type="button"
          pending={vm.isTrackDayPending(day)}
          focusedDay={day === focusDay}
          dimmed={focusDay != null && day !== focusDay}
          aria-pressed={day === focusDay}
          onClick={() => vm.focusTrackDay(day)}
        >
          {/* The swatch only means something when colour is keyed to date;
              otherwise it would imply a mapping the map isn't using. */}
          {vm.trackColorMode === "date" && !focusDay && <Swatch color={color} />}
          <Text size={12}>{moment(day).format("D MMM")}</Text>
        </DayRow>
      ))}

      {vm.tracksLoading && vm.trackLoadProgress.total > 0 && (
        <>
          <Bar>
            <BarFill pct={vm.trackLoadProgress.pct} />
          </Bar>
          <Muted>
            {`${lang.fields.tracksLoadingDays ?? "Loading days"} ${vm.trackLoadProgress.loaded}/${vm.trackLoadProgress.total}`}
          </Muted>
        </>
      )}

      {/* A background rescan can run for a minute on a busy fleet. Without a
          visible marker the panel looked idle and finished, so the days it was
          still about to find looked like they were simply missing. */}
      {vm.tracksRefreshing && !vm.tracksLoading && (
        <>
          <SizedBox height={8} />
          <Pulsing>
            <Muted>
              {lang.fields.tracksUpdating ?? "Checking for more days…"}
            </Muted>
          </Pulsing>
        </>
      )}

      {vm.tracksTruncated && (
        <>
          <SizedBox height={8} />
          <Muted>
            {lang.fields.tracksTruncated ??
              "Too many points to draw at once — deselect some days."}
          </Muted>
        </>
      )}

      {vm.tracksComputedThrough && (
        <>
          <SizedBox height={8} />
          <Muted>
            {`${lang.fields.tracksAsOf ?? "Updated"} ${moment(
              vm.tracksComputedThrough
            ).format("D MMM HH:mm")}`}
          </Muted>
        </>
      )}

      {!isMobile && (
        <HideLegendButton type="button" onClick={() => setHidden(true)}>
          {lang.fields.legendHideLabel ?? "Hide"}
        </HideLegendButton>
      )}
    </Root>
  );
};

export default observer(FieldTracksPanel);
